import React from 'react';
import { StructuredIngredient } from '@/types/recipe';
import { cn } from '@/lib/utils';
import { formatSmartQuantity } from '@/utils/quantityFormatter';

interface RecipeIngredientsListProps {
  structuredIngredients?: StructuredIngredient[] | null;
}

const RecipeIngredientsList: React.FC<RecipeIngredientsListProps> = ({ 
  structuredIngredients 
}) => {
  if (!structuredIngredients || structuredIngredients.length === 0) {
    return (
      <div className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Ingredients</h2>
        <p className="text-muted-foreground">No ingredients available</p>
      </div>
    );
  }

  // Helper to format ingredient quantity with smart formatting
  const formatIngredientQuantity = (ingredient: StructuredIngredient): string => {
    if (ingredient.quantity === null) return "";
    
    // Create a quantity string combining number and unit for formatting
    const quantityWithUnit = ingredient.unit 
      ? `${ingredient.quantity} ${ingredient.unit}`
      : ingredient.quantity.toString();
    
    // Apply smart formatting
    const formatted = formatSmartQuantity(quantityWithUnit);
    
    // If there's no unit in the original ingredient, return just the formatted number
    if (!ingredient.unit) {
      return formatted;
    }
    
    // Extract the formatted number and keep the original unit for color coding
    const match = formatted.match(/^([\d.]+)\s*(.*)$/);
    if (match) {
      return match[1]; // Return just the formatted number, unit will be displayed separately
    }
    
    return ingredient.quantity.toString(); // Fallback
  };
  
  return (
    <div className="mb-8">
      <h2 className="text-2xl font-semibold mb-4">Ingredients</h2>
      <ul className="list-disc pl-5 space-y-2">
        {structuredIngredients.map((ingredient, index) => (
          <li key={index} className="flex items-start">
            {ingredient.quantity !== null && (
              <span 
                className={cn(
                  "font-medium mr-1",
                  ingredient.unit ? "" : "mr-2" // Add more spacing if no unit
                )}
              >
                {formatIngredientQuantity(ingredient)}
              </span>
            )}
            {ingredient.unit && (
              <span 
                className={cn(
                  "mr-2",
                  // Color code based on unit system
                  ingredient.unit.match(/g$|kg$|ml$|l$|cm$|mm$|°C$|C$/) 
                    ? "text-blue-600 dark:text-blue-400" 
                    : ingredient.unit.match(/oz$|lb$|cup|tbsp|tsp|°F$|F$|in$|ft$/) 
                      ? "text-green-600 dark:text-green-400"
                      : ""
                )}
              >
                {ingredient.unit}
              </span>
            )}
            <span>{ingredient.name}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default RecipeIngredientsList;
